import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

var hasRegisteredPlugin = false;
var TARGET_ATTRIBUTE = 'data-scroll-text-reveal';
var SPLIT_ATTRIBUTE = 'data-scroll-text-split-original';
var LINE_MASK_CLASS = 'js-scroll-text-line-mask';
var LINE_CLASS = 'js-scroll-text-line';
var WORD_CLASS = 'js-scroll-text-word';
var PREPARED_CLASS = 'js-scroll-text-reveal';
var EYEBROW_SELECTOR = '[class*="eyebrow"]';
var HEADING_SELECTOR = 'h1, h2, h3, .events-here__heading';
var EXCLUDED_SELECTOR = '.open-roles-modal, .case-study__accordion-heading, .our-team__item-heading';
var ALLOWED_INLINE_TAGS = {
  A: true,
  ABBR: true,
  B: true,
  BR: true,
  CITE: true,
  EM: true,
  I: true,
  MARK: true,
  SMALL: true,
  SPAN: true,
  STRONG: true,
  SUB: true,
  SUP: true,
  U: true,
};

function registerScrollTrigger() {
  if (hasRegisteredPlugin) {
    return;
  }

  gsap.registerPlugin(ScrollTrigger, SplitText);
  hasRegisteredPlugin = true;
}

function splitHeadingWithSplitText(headingElement) {
  if (!headingElement || typeof SplitText !== 'function') {
    return null;
  }

  try {
    var splitInstance = new SplitText(headingElement, {
      type: 'lines',
      mask: 'lines',
      linesClass: LINE_CLASS,
    });

    if (!splitInstance || !splitInstance.lines || !splitInstance.lines.length) {
      if (splitInstance && typeof splitInstance.revert === 'function') {
        splitInstance.revert();
      }
      return null;
    }

    return {
      lines: splitInstance.lines,
      revert: function revertSplitText() {
        splitInstance.revert();
      },
    };
  } catch (error) {
    return null;
  }
}

function isElementInSection(element, section) {
  return !!(element && section && section.contains(element));
}

function isEligibleTarget(element) {
  if (!element || !(element instanceof HTMLElement)) {
    return false;
  }

  if (!element.textContent || !element.textContent.trim()) {
    return false;
  }

  if (element.closest(EXCLUDED_SELECTOR)) {
    return false;
  }

  return true;
}

function prepareTargetElement(element) {
  if (!isEligibleTarget(element)) {
    return false;
  }

  element.classList.add(PREPARED_CLASS);
  element.classList.add('js-scroll-animate');
  element.setAttribute(TARGET_ATTRIBUTE, 'true');

  return true;
}

function canSplitHeading(headingElement) {
  if (!headingElement) {
    return false;
  }

  var descendantElements = Array.prototype.slice.call(headingElement.querySelectorAll('*'));

  return descendantElements.every(function allowOnlyInlineTags(descendantElement) {
    return !!ALLOWED_INLINE_TAGS[descendantElement.tagName];
  });
}

function tokenizeHeadingTextNodes(headingElement) {
  var textNodes = [];
  var walker = document.createTreeWalker(headingElement, NodeFilter.SHOW_TEXT, null);

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach(function replaceTextNode(textNode) {
    var textValue = textNode.nodeValue || '';
    var tokens = textValue.match(/\S+\s*/g);

    if (!tokens || !tokens.length) {
      // Preserve inter-element spacing so inline-tag boundaries keep readable word gaps.
      textNode.nodeValue = /\s/.test(textValue) ? ' ' : '';
      return;
    }

    var fragment = document.createDocumentFragment();

    tokens.forEach(function appendToken(token) {
      var wordElement = document.createElement('span');
      wordElement.className = WORD_CLASS;
      wordElement.textContent = token;
      fragment.appendChild(wordElement);
    });

    textNode.parentNode.replaceChild(fragment, textNode);
  });
}

function wrapHeadingLinesFromWords(headingElement, lineGroups) {
  var lineElements = [];

  for (var groupIndex = lineGroups.length - 1; groupIndex >= 0; groupIndex--) {
    var group = lineGroups[groupIndex];

    if (!group || !group.length) {
      continue;
    }

    var firstWord = group[0];
    var lastWord = group[group.length - 1];
    var range = document.createRange();
    var lineMask = document.createElement('span');
    var line = document.createElement('span');

    range.setStartBefore(firstWord);
    range.setEndAfter(lastWord);

    lineMask.className = LINE_MASK_CLASS;
    line.className = LINE_CLASS;
    line.appendChild(range.extractContents());
    lineMask.appendChild(line);
    headingElement.insertBefore(lineMask, headingElement.firstChild);
    lineElements.unshift(line);
  }

  Array.prototype.slice.call(headingElement.childNodes).forEach(function removeStrays(node) {
    if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains(LINE_MASK_CLASS)) {
      return;
    }

    headingElement.removeChild(node);
  });

  return lineElements;
}

function splitHeadingIntoLines(headingElement) {
  if (!headingElement || !canSplitHeading(headingElement)) {
    return [];
  }

  if (headingElement.hasAttribute(SPLIT_ATTRIBUTE)) {
    return Array.prototype.slice.call(headingElement.querySelectorAll('.' + LINE_CLASS));
  }

  var originalText = (headingElement.textContent || '').replace(/\s+/g, ' ').trim();

  if (!originalText) {
    return [];
  }

  headingElement.setAttribute(SPLIT_ATTRIBUTE, headingElement.innerHTML);
  tokenizeHeadingTextNodes(headingElement);

  var wordElements = Array.prototype.slice.call(headingElement.querySelectorAll('.' + WORD_CLASS));

  if (!wordElements.length) {
    return [];
  }

  var lineGroups = [];
  var currentTop = null;

  wordElements.forEach(function collectWord(wordElement) {
    var wordTop = Math.round(wordElement.offsetTop);

    if (currentTop === null || Math.abs(wordTop - currentTop) > 2) {
      lineGroups.push([]);
      currentTop = wordTop;
    }

    lineGroups[lineGroups.length - 1].push(wordElement);
  });

  return wrapHeadingLinesFromWords(headingElement, lineGroups);
}

function revertSplitHeading(headingElement) {
  if (!headingElement || !headingElement.hasAttribute(SPLIT_ATTRIBUTE)) {
    return;
  }

  headingElement.innerHTML = headingElement.getAttribute(SPLIT_ATTRIBUTE) || '';
  headingElement.removeAttribute(SPLIT_ATTRIBUTE);
}

function findPairedEyebrow(headingElement, sectionElement, eyebrowElements, usedEyebrows) {
  if (!isElementInSection(headingElement, sectionElement)) {
    return null;
  }

  var headingRect = headingElement.getBoundingClientRect();
  var headingTop = headingRect.top + window.scrollY;
  var maxDistance = 260;

  var matches = eyebrowElements.filter(function isCandidate(eyebrowElement) {
    if (usedEyebrows.has(eyebrowElement)) {
      return false;
    }

    if (!isElementInSection(eyebrowElement, sectionElement)) {
      return false;
    }

    if (!(eyebrowElement.compareDocumentPosition(headingElement) & Node.DOCUMENT_POSITION_FOLLOWING)) {
      return false;
    }

    var eyebrowRect = eyebrowElement.getBoundingClientRect();
    var eyebrowTop = eyebrowRect.top + window.scrollY;
    var distance = headingTop - eyebrowTop;

    return distance > 0 && distance < maxDistance;
  });

  if (!matches.length) {
    return null;
  }

  matches.sort(function byNearest(a, b) {
    var aTop = a.getBoundingClientRect().top + window.scrollY;
    var bTop = b.getBoundingClientRect().top + window.scrollY;
    return bTop - aTop;
  });

  return matches[0] || null;
}

function buildAnimationGroups(sectionElement) {
  var eyebrowElements = Array.prototype.slice
    .call(sectionElement.querySelectorAll(EYEBROW_SELECTOR))
    .filter(isEligibleTarget);

  var headingElements = Array.prototype.slice
    .call(sectionElement.querySelectorAll(HEADING_SELECTOR))
    .filter(isEligibleTarget);

  var usedEyebrows = new Set();
  var groups = [];

  headingElements.forEach(function pairHeading(headingElement) {
    var pairedEyebrow = findPairedEyebrow(headingElement, sectionElement, eyebrowElements, usedEyebrows);

    if (pairedEyebrow) {
      usedEyebrows.add(pairedEyebrow);
    }

    groups.push({
      eyebrow: pairedEyebrow,
      heading: headingElement,
    });
  });

  eyebrowElements.forEach(function addUnpairedEyebrow(eyebrowElement) {
    if (usedEyebrows.has(eyebrowElement)) {
      return;
    }

    groups.push({
      eyebrow: eyebrowElement,
      heading: null,
    });
  });

  return groups;
}

export function prepareScrollTextRevealTargets() {
  if (typeof document === 'undefined') {
    return;
  }

  var sections = Array.prototype.slice.call(document.querySelectorAll('.site-main .home-section'));

  sections.forEach(function prepareSection(sectionElement) {
    var candidates = Array.prototype.slice.call(sectionElement.querySelectorAll(EYEBROW_SELECTOR + ', ' + HEADING_SELECTOR));

    candidates.forEach(function prepareCandidate(element) {
      prepareTargetElement(element);
    });
  });
}

export function initializeScrollTextRevealAnimations() {
  if (typeof window === 'undefined') {
    return function noop() {};
  }

  registerScrollTrigger();

  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (reduceMotionQuery.matches) {
    var reducedElements = Array.prototype.slice.call(document.querySelectorAll('.' + PREPARED_CLASS));

    reducedElements.forEach(function revealReducedElement(element) {
      gsap.set(element, {
        clearProps: 'opacity,visibility,transform,will-change',
      });
    });

    return function noop() {};
  }

  var sections = Array.prototype.slice.call(document.querySelectorAll('.site-main .home-section'));
  var triggers = [];
  var splitCleanups = [];

  sections.forEach(function setupSection(sectionElement) {
    var groups = buildAnimationGroups(sectionElement);

    groups.forEach(function setupGroup(group) {
      var eyebrowElement = group.eyebrow;
      var headingElement = group.heading;
      var headingLines = [];

      if (eyebrowElement && !prepareTargetElement(eyebrowElement)) {
        eyebrowElement = null;
      }

      if (headingElement && !prepareTargetElement(headingElement)) {
        headingElement = null;
      }

      if (!eyebrowElement && !headingElement) {
        return;
      }

      if (headingElement) {
        var splitResult = splitHeadingWithSplitText(headingElement);

        if (splitResult && splitResult.lines.length) {
          headingLines = splitResult.lines;
          splitCleanups.push(splitResult.revert);
        } else {
          headingLines = splitHeadingIntoLines(headingElement);

          if (headingLines.length) {
            splitCleanups.push(function revertCustomSplit() {
              revertSplitHeading(headingElement);
            });
          }
        }
      }

      var timeline = gsap.timeline({
        defaults: {
          ease: 'power3.out',
        },
        scrollTrigger: {
          trigger: headingElement || eyebrowElement,
          start: 'top 78%',
          once: true,
        },
      });

      if (eyebrowElement) {
        gsap.set(eyebrowElement, {
          autoAlpha: 0,
          y: 18,
          willChange: 'transform,opacity',
        });

        timeline.to(eyebrowElement, {
          autoAlpha: 1,
          y: 0,
          duration: 0.78,
          clearProps: 'will-change',
        }, 0);
      }

      if (headingElement) {
        if (headingLines.length) {
          gsap.set(headingElement, {
            autoAlpha: 1,
          });

          gsap.set(headingLines, {
            yPercent: 100,
            autoAlpha: 0,
            willChange: 'transform,opacity',
          });

          timeline.to(headingLines, {
            yPercent: 0,
            autoAlpha: 1,
            duration: 0.72,
            stagger: 0.08,
            clearProps: 'will-change',
          }, eyebrowElement ? 0.24 : 0);
        } else {
          gsap.set(headingElement, {
            autoAlpha: 0,
            y: 22,
            willChange: 'transform,opacity',
          });

          timeline.to(headingElement, {
            autoAlpha: 1,
            y: 0,
            duration: 0.72,
            clearProps: 'will-change',
          }, eyebrowElement ? 0.24 : 0);
        }
      }

      if (timeline.scrollTrigger) {
        triggers.push(timeline.scrollTrigger);
      }
    });
  });

  return function cleanupTextRevealAnimations() {
    triggers.forEach(function killTrigger(trigger) {
      if (trigger && typeof trigger.kill === 'function') {
        trigger.kill();
      }
    });

    splitCleanups.forEach(function runSplitCleanup(cleanup) {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    });

    var preparedElements = Array.prototype.slice.call(document.querySelectorAll('.' + PREPARED_CLASS));

    preparedElements.forEach(function clearPreparedProps(element) {
      gsap.set(element, {
        clearProps: 'opacity,visibility,transform,will-change',
      });
      element.removeAttribute(TARGET_ATTRIBUTE);
      element.classList.remove(PREPARED_CLASS);
      element.classList.remove('js-scroll-animate');
    });
  };
}
